import { Info, Share } from '@drift-labs/icons';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	OrderType,
	PRICE_PRECISION_EXP,
	PerpMarketConfig,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import {
	COMMON_UI_UTILS,
	ENUM_UTILS,
	OpenPosition,
	UIOrderType,
	UI_ORDER_TYPES_LIST,
	matchEnum,
	MarketId,
} from '@drift/common';
import { useCallback, useMemo, useState } from 'react';
import Env, {
	OrderedPerpMarkets,
	PERP_MARKETS_LOOKUP,
} from 'src/environmentVariables/EnvironmentVariables';
import { useTickedAccountData } from 'src/hooks/useAccountData';
import useDriftActions from 'src/hooks/useDriftActions';
import useDriftClient from 'src/hooks/useDriftClient';
import useIsMobile from 'src/hooks/useIsMobileScreenSize';
import useLocalStorageState from 'src/hooks/useLocalStorageState';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import NumLib from 'src/utils/NumLib';
import useShowAccountValues from '../hooks/useShowAccountValues';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import Button from './Button';
import TableActionButton from './Buttons/TableActionButton';
import PaginatedTableV2 from './PaginatedTableV2';
import ClosePositionPopup from './Popups/ClosePositionPopup';
import TableV2 from './Tables/TableV2';
import Text from './Text/Text';
import Tooltip from './Tooltip/Tooltip';
import NumberDisplay from './Utils/NumberDisplay';
import ValueChangeBlink from './Utils/ValueChangeBlink';
import Chevron from './Chevron';
import TargettedPopup from './Popups/TargettedPopup';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/router';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useMarketStateStore from '../stores/useMarketStateStore';
import CheckboxInput from './CheckboxInput';
import React from 'react';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useWindowSizeString from 'src/hooks/useWindowSize';
import EditOrderPopup from './Popups/EditOrderPopup';
import { TakeProfitStopLossButton } from './Buttons/TakeProfitStopLossButton';

interface AccountOpenPosition extends OpenPosition {
	accountName: string;
	accountKey: string;
	isLpPosition?: boolean;
	isDustPosition?: boolean;
	showLpEntryPriceTooltip?: boolean;
}

interface ClosePositionOptions {
	accountKey: string;
	market: number;
	closePositionOrderType?: UIOrderType;
}

interface OpenPositionTableRowProps {
	position: AccountOpenPosition;
	tableGrid: string;
	smallTableGrid: string;
	small: boolean;
	isMobile: boolean;
	pnlReference: 'mark' | 'oracle';
	handleClosePosition: (options: ClosePositionOptions) => void;
	handleSelection: (market: PerpMarketConfig) => void;
	handleCopySize: (size: string) => void;
	handleCopyTradePrice: (price: number) => void;
	handleShowPnlModal: (position: OpenPosition, pnl: number) => void;
	handleEditLp: (marketIndex: number) => void;
	handleViewLpDetails: (marketIndex: number) => void;
}

const pageSize = 8;
const CLOSE_POSITION_ORDER_TYPES: OrderType[] = [
	OrderType.LIMIT,
	OrderType.MARKET,
	OrderType.TRIGGER_LIMIT,
	OrderType.TRIGGER_MARKET,
];

const OpenPositionTableRow = ({
	position,
	tableGrid,
	smallTableGrid,
	small,
	pnlReference,
	isMobile,
	handleClosePosition,
	handleSelection,
	handleCopySize,
	handleCopyTradePrice,
	handleShowPnlModal,
	handleEditLp,
	handleViewLpDetails,
}: OpenPositionTableRowProps) => {
	const {
		marketSymbol,
		marketIndex,
		direction,
		entryPrice,
		liqPrice,
		accountKey,
		baseSize,
	} = position;

	const baseSizeBigNum = BigNum.from(baseSize.abs(), BASE_PRECISION_EXP);

	const isLong = direction === 'long';
	const orderTypeDropdownElementId = `order_type_dropdown_${marketSymbol}`;

	const router = useRouter();
	const [isMoreOrderTypesDropdownOpen, setIsMoreOrderTypesDropdownOpen] =
		useState(false);
	const marketId = useMemo(() => {
		return MarketId.createPerpMarket(marketIndex);
	}, [position]);

	const getMarkPriceForMarket = useMarketStateStore(
		(s) => s.getMarkPriceForMarket
	);
	const markPrice = getMarkPriceForMarket(marketId);

	const getOraclePriceForMarket = useMarketStateStore(
		(s) => s.getOraclePriceForMarket
	);
	const oraclePrice = getOraclePriceForMarket(marketId);

	const [userSettings] = useCurrentSettings();
	const accountLeverage =
		useDriftAccountStore(
			(s) => s.accounts[s.currentUserKey]?.marginInfo?.leverage
		) ?? 1;
	const advancedLp =
		useDriftAccountStore(
			(s) => s.accounts[s.currentUserKey]?.marginInfo?.advancedLp
		) ?? false;

	const isOverviewPage = router.asPath.includes('overview');

	const notional = BigNum.from(
		position.notional,
		QUOTE_PRECISION_EXP
	).toNotional();

	const showAccountValues = useShowAccountValues();

	const pnlToUse =
		pnlReference === 'mark' ? position.pnlVsMark : position.pnlVsOracle;
	const pnl = NumLib.formatBn.fromQuote(pnlToUse);

	const dollarPnl = showAccountValues
		? `${NumLib.formatNum.toNotionalDisplay(pnl)}`
		: '∗∗∗∗∗∗';

	const percentageNum = COMMON_UI_UTILS.calculatePnlPctFromPosition(
		pnlToUse,
		position.isLpPosition ? position.notional : position.quoteEntryAmount,
		userSettings.includeLeverageInPnl ? accountLeverage : 1
	);

	const percentagePnl = `${percentageNum.toFixed(2)}%`;

	const handleClickMarket = () => {
		handleSelection(PERP_MARKETS_LOOKUP[marketIndex]);
	};

	const handleSelectCloseOrderType = (closePositionOrderType: UIOrderType) => {
		setIsMoreOrderTypesDropdownOpen(false);
		handleClosePosition({
			accountKey,
			market: position.marketIndex,
			closePositionOrderType: closePositionOrderType,
		});
	};

	return isMobile ? (
		<TableV2.ResponsiveTableRow
			grid={isMobile ? undefined : small ? smallTableGrid : tableGrid}
			marketString={marketSymbol}
			direction={isLong ? PositionDirection.LONG : PositionDirection.SHORT}
			action={{
				label: 'Close',
				onClick: () => {
					handleClosePosition({ accountKey, market: position.marketIndex });
				},
				positive: pnl >= 0,
			}}
			isDataRow
			className="p-4 m-4 border whitespace-nowrap border-container-border"
		>
			<TableV2.CardValue
				className="font-numeral"
				label="Size"
				highlight
				value={
					<button
						onClick={() => handleCopySize(baseSizeBigNum.toTradePrecision())}
						className="flex space-x-1 text-left"
					>
						{`${
							showAccountValues ? baseSizeBigNum.toTradePrecision() : '∗∗∗∗'
						} ${COMMON_UI_UTILS.getBaseAssetSymbol(marketSymbol)}`}
					</button>
				}
			/>

			<TableV2.CardValue
				className="font-numeral "
				label="Notional"
				highlight
				value={
					<ValueChangeBlink value={showAccountValues ? notional : '$ ∗∗∗∗∗∗'}>
						{showAccountValues ? notional : '$ ∗∗∗∗∗∗'}
					</ValueChangeBlink>
				}
			/>
			<TableV2.CardValue
				className="font-numeral "
				label="Mark Price"
				value={
					<NumberDisplay
						displayType="tradePrecision"
						value={markPrice?.toNum()}
						subType="$"
					/>
				}
			/>
			<TableV2.CardValue
				className="font-numeral "
				label="Entry Price"
				value={
					<button
						onClick={() =>
							handleCopyTradePrice(
								BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum()
							)
						}
					>
						<NumberDisplay
							displayType="tradePrecision"
							value={BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum()}
							subType="$"
							//textOverride={position.isDustPosition ? '-' : undefined}
						/>
					</button>
				}
			/>
			<TableV2.CardValue
				className="font-numeral"
				label="Liq. Price"
				value={
					<ValueChangeBlink
						value={
							showAccountValues
								? BigNum.from(liqPrice, PRICE_PRECISION_EXP).toNotional(true)
								: '$ ∗∗∗∗∗∗'
						}
					>
						{showAccountValues
							? BigNum.from(liqPrice, PRICE_PRECISION_EXP).lteZero()
								? 'None'
								: BigNum.from(liqPrice, PRICE_PRECISION_EXP).toNotional(true)
							: '$ ∗∗∗∗∗∗'}
					</ValueChangeBlink>
				}
			/>

			<TableV2.CardValue
				className={`inline-flex font-numeral ${
					pnl > 0 ? `text-positive-green` : pnl < 0 ? `text-negative-red` : ``
				}`}
				label="P&amp;L"
				icon={
					<img src={`/assets/icons/pnlCash.svg`} className="w-4 h-4 ml-0.5" />
				}
				value={
					<span className="relative flex inline-flex items-center w-full">
						<ValueChangeBlink value={dollarPnl}>
							<span className="w-24">{dollarPnl}</span>
						</ValueChangeBlink>
						<ValueChangeBlink value={percentagePnl}>
							<span className="w-24 ml-1">({percentagePnl})</span>
						</ValueChangeBlink>
						{!small && (
							<div
								className="flex justify-end hover:opacity-80 hover:cursor-pointer mb-0.5 ml-0.5 inline-flex"
								onClick={() => handleShowPnlModal(position, pnl)}
							>
								<Share color="var(--text-label)" size={20} />
							</div>
						)}
					</span>
				}
			/>

			<TableV2.CardValue
				className="font-numeral"
				label="Take Profit"
				value={
					<TakeProfitStopLossButton type="takeProfit" position={position} />
				}
			></TableV2.CardValue>

			<TableV2.CardValue
				className="font-numeral"
				label="Stop Loss"
				value={<TakeProfitStopLossButton type="stopLoss" position={position} />}
			></TableV2.CardValue>

			{position.isLpPosition && (
				<>
					<TableActionButton
						className="justify-start"
						label="LP"
						onClick={() => {
							handleEditLp(position.marketIndex);
						}}
						neutral
						positive={false}
						id={`editLp_${position.marketIndex.toString()}_${accountKey}`}
						hideOnMobile
					/>
					<Button.Secondary
						iconOnly
						size="SMALL"
						onClick={() => handleViewLpDetails(position.marketIndex)}
					>
						<Info className="h-4 text-text-default" />
					</Button.Secondary>
				</>
			)}
		</TableV2.ResponsiveTableRow>
	) : (
		<TableV2.BodyRow grid={small ? smallTableGrid : tableGrid} isDataRow>
			{/** Desktop - Market + Direction */}
			<TableV2.OpenPositionMarketDirectionCell
				handleClickMarket={handleClickMarket}
				position={position}
			/>

			{/** Desktop - Size */}
			<TableV2.BodyCell className="font-numeral text-text-default">
				<span className="flex flex-col">
					<button
						onClick={() => handleCopySize(baseSizeBigNum.toTradePrecision())}
						className="flex space-x-1 text-left"
					>
						{`${
							showAccountValues ? baseSizeBigNum.toTradePrecision() : '∗∗∗∗'
						} ${COMMON_UI_UTILS.getBaseAssetSymbol(marketSymbol)}`}
					</button>
					<ValueChangeBlink value={showAccountValues ? notional : '$ ∗∗∗∗∗∗'}>
						<span className="text-text-secondary">
							{showAccountValues ? notional : '$ ∗∗∗∗∗∗'}
						</span>
					</ValueChangeBlink>
				</span>
			</TableV2.BodyCell>

			{/** Desktop - Entry/Mark Price */}
			{!small && (
				<>
					<TableV2.BodyCell className="font-numeral text-text-default">
						<button
							onClick={() =>
								handleCopyTradePrice(
									BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum()
								)
							}
						>
							{/* Tooltip required if position is lp position from before the entry price program update */}
							{position.showLpEntryPriceTooltip ? (
								<Tooltip content="Entry price for older BAL positions may be inaccurate, please reference Unsettled P&L tab for actual performance">
									<NumberDisplay
										displayType="tradePrecision"
										value={BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum()}
										subType="$"
										//textOverride={position.isDustPosition ? '-' : undefined}
									/>
								</Tooltip>
							) : (
								<NumberDisplay
									displayType="tradePrecision"
									value={BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum()}
									subType="$"
									//textOverride={position.isDustPosition ? '-' : undefined}
								/>
							)}

							<Text.BODY3 className="text-text-secondary">
								{pnlReference === 'mark' ? (
									<NumberDisplay
										displayType="tradePrecision"
										value={markPrice?.toNum()}
										subType="$"
									/>
								) : (
									<NumberDisplay
										displayType="tradePrecision"
										value={oraclePrice?.toNum()}
										subType="$"
									/>
								)}
							</Text.BODY3>
						</button>
					</TableV2.BodyCell>
				</>
			)}

			{/** Desktop - PnL */}
			<TableV2.BodyCell
				className={`font-numeral ${
					pnl > 0 ? `text-positive-green` : pnl < 0 ? `text-negative-red` : ``
				}`}
			>
				<span className="relative flex items-center w-full">
					<Tooltip
						placement="top"
						content={
							<>
								<div className="flex flex-col items-start mb-2 space-y-2">
									<span>
										P&L based on current{' '}
										{pnlReference === 'mark' ? 'mark price' : 'oracle price'}.
										P&L upon closing your position may differ due to exit price.
									</span>
								</div>
							</>
						}
					>
						<ValueChangeBlink value={dollarPnl}>
							<div className="flex flex-col">
								<span>{dollarPnl}</span>
								{/* {!position.isDustPosition && ( */}
								<Text.MICRO1>{percentagePnl}</Text.MICRO1>
								{/* )} */}
							</div>
						</ValueChangeBlink>
					</Tooltip>

					{!small && (
						<span
							className="flex justify-end w-full hover:opacity-80 hover:cursor-pointer mb-0.5 ml-1"
							onClick={() => handleShowPnlModal(position, pnl)}
						>
							<Share color="var(--text-label)" size={20} />
						</span>
					)}
				</span>
			</TableV2.BodyCell>

			{/** Desktop - TP */}
			{!small && (
				<TableV2.BodyCell>
					<TakeProfitStopLossButton type="takeProfit" position={position} />
				</TableV2.BodyCell>
			)}

			{/** Desktop - SL */}
			{!small && (
				<TableV2.BodyCell>
					<TakeProfitStopLossButton type="stopLoss" position={position} />
				</TableV2.BodyCell>
			)}

			{/** Desktop - Liq. Price */}
			{!small && (
				<TableV2.BodyCell className="font-numeral text-text-default">
					<span className="flex flex-col">
						<ValueChangeBlink
							value={
								showAccountValues
									? BigNum.from(liqPrice, PRICE_PRECISION_EXP).toNotional(true)
									: '$ ∗∗∗∗∗∗'
							}
						>
							{showAccountValues
								? BigNum.from(liqPrice, PRICE_PRECISION_EXP).lteZero()
									? 'None'
									: BigNum.from(liqPrice, PRICE_PRECISION_EXP).toNotional(true)
								: '$ ∗∗∗∗∗∗'}
						</ValueChangeBlink>
						{position.isLpPosition && !advancedLp && (
							<Tooltip
								content={`If the oracle price of ${
									OrderedPerpMarkets[position.marketIndex].symbol
								} reaches ${BigNum.from(
									position.lpDeriskPrice,
									PRICE_PRECISION_EXP
								).toNotional(
									true
								)}, de-risking of BAL shares and position will be triggered by the next P&L settle.`}
							>
								<Text.BODY3 className="text-text-secondary">
									<ValueChangeBlink
										value={
											showAccountValues
												? BigNum.from(
														position.lpDeriskPrice,
														PRICE_PRECISION_EXP
												  ).toNotional(true)
												: '$ ∗∗∗∗∗∗'
										}
									>
										{showAccountValues
											? BigNum.from(
													position.lpDeriskPrice,
													PRICE_PRECISION_EXP
											  ).lteZero()
												? 'None'
												: BigNum.from(
														position.lpDeriskPrice,
														PRICE_PRECISION_EXP
												  ).toNotional(true)
											: '$ ∗∗∗∗∗∗'}
									</ValueChangeBlink>
								</Text.BODY3>
							</Tooltip>
						)}
					</span>
				</TableV2.BodyCell>
			)}

			{/** Desktop - Action buttons */}
			<TableV2.BodyCell>
				<div className="inline-flex items-center gap-[2px]">
					<TableActionButton
						className="justify-start px-0"
						buttonClassName="rounded-l-sm rounded-r-none"
						label="Close"
						onClick={() => {
							handleClosePosition({ accountKey, market: position.marketIndex });
						}}
						positive={!isLong}
						id={`closePosition_${position.marketIndex.toString()}_${accountKey}`}
					/>

					<Button.Secondary
						id={orderTypeDropdownElementId}
						size="SMALL"
						onClick={() => setIsMoreOrderTypesDropdownOpen((prev) => !prev)}
						className={twMerge(
							'py-1 bg-negative-red-secondary-bg hover:bg-negative-red-hover text-negative-red hover:text-negative-red-secondary-bg rounded-l-none rounded-r-sm',
							isLong
								? 'bg-negative-red-secondary-bg hover:bg-negative-red-hover text-negative-red hover:text-negative-red-secondary-bg'
								: 'bg-positive-green-secondary-bg hover:bg-positive-green-hover text-positive-green hover:text-positive-green-secondary-bg'
						)}
					>
						<Chevron open={isMoreOrderTypesDropdownOpen} />
					</Button.Secondary>
					{isMoreOrderTypesDropdownOpen && (
						<TargettedPopup
							targetElementId={orderTypeDropdownElementId}
							side={isOverviewPage ? 'bottomRight' : 'top'}
							className="w-[146px]"
							noBorder
							onClose={() => setIsMoreOrderTypesDropdownOpen(false)}
						>
							<div className="flex flex-col border divide-y divide-container-border border-container-border first:rounded-t last:rounded-b">
								{UI_ORDER_TYPES_LIST.filter((uiOrderType) =>
									CLOSE_POSITION_ORDER_TYPES.some((includedOrderType) =>
										matchEnum(includedOrderType, uiOrderType.orderType)
									)
								).map((orderType) => (
									<div
										key={orderType.value}
										className="px-2 py-1 cursor-pointer hover:bg-container-bg-hover"
										onClick={() => handleSelectCloseOrderType(orderType.value)}
									>
										{orderType.label}
									</div>
								))}
							</div>
						</TargettedPopup>
					)}

					{position.isLpPosition && (
						<Button.Secondary
							id={`editLp_${position.marketIndex.toString()}_${accountKey}`}
							size="SMALL"
							onClick={() => {
								handleEditLp(position.marketIndex);
							}}
						>
							BAL
						</Button.Secondary>
					)}
				</div>
			</TableV2.BodyCell>
		</TableV2.BodyRow>
	);
};

const OpenPositionsTable = (props: {
	small?: boolean;
	overviewPage?: boolean;
}) => {
	const closePositionPopupTargetId = useDriftStore(
		(s) => s.popups.closePositionPopupOptions.popupTargetId
	);
	const closePositionOrderType = useDriftStore(
		(s) => s.popups.closePositionPopupOptions.popupOrderType
	);
	const showClosePositionPopupForMarketIndex = useDriftStore(
		(s) => s.popups.closePositionPopupOptions.marketIndex
	);
	const closePositionPopupType = useDriftStore(
		(s) => s.popups.closePositionPopupOptions.popupType
	);

	// For editing TP/SL orders from inside the position row
	const orderToEdit = useDriftStore(
		(s) => s.popups.editOrderPopupOptions.orderToEdit
	);
	const editOrderTargetElementId = useDriftStore(
		(s) => s.popups.editOrderPopupOptions.popupTargetId
	);

	const hideClosePositionPopup = useDriftStore(
		(s) => s.popups.hideClosePositionPopup
	);
	const showClosePositionPopup = useDriftStore(
		(s) => s.popups.showClosePositionPopup
	);
	const hideEditOrderPopup = useDriftStore((s) => s.popups.hideEditOrderPopup);

	const setStore = useDriftStore((s) => s.set);

	const showOnlyCurrentMarket =
		useDriftStore((s) => s.showOnlyCurrentMarket) && !props.overviewPage;

	const selectedMarketIndex = useDriftStore(
		(s) => s.selectedMarket?.current?.market?.marketIndex
	);

	const windowSize = useWindowSizeString();

	const marketDirectionColumnSize =
		windowSize === 'xl' ? 'minmax(130px, 11fr)' : 'minmax(100px, 8fr)';

	const tableGrid = `${marketDirectionColumnSize} minmax(110px,10fr) minmax(110px,10fr)  minmax(100px,9fr) minmax(105px,9fr) minmax(105px,9fr)minmax(100px,9fr) minmax(160px,5fr)`;
	const smallTableGrid = `${marketDirectionColumnSize} minmax(95px,10fr) minmax(95px,8fr) minmax(130px,10fr)`;

	const currentAccount = useTickedAccountData();
	const driftClient = useDriftClient();
	const setAccountStore = useDriftAccountStore((s) => s.set);
	const actions = useDriftActions();
	const perpMarketInfoLookup =
		useMarketsInfoStore()?.allMarketsInfo?.perpLookup;

	const displayPositions = getDisplayPosition();
	const filteredPositions = displayPositions
		? showOnlyCurrentMarket
			? displayPositions.filter(
					(pos) => pos.marketIndex === selectedMarketIndex
			  )
			: displayPositions
		: undefined;

	const resetState = () => {
		setFirstPageShown(0);
		setCurrentPage(0);
	};

	useWalletIsConnected({ onDisconnectedCallback: resetState });

	function getDisplayPosition() {
		if (!currentAccount || !currentAccount?.positionsLoaded) return undefined;

		const lpMarketIndexes = currentAccount.openPerpPositions
			.filter((pos) => !pos.lpShares.eq(ZERO))
			.map((pos) => pos.marketIndex);

		const lastLpAdded =
			currentAccount.client
				.getUserAccount()
				.lastAddPerpLpSharesTs?.toNumber() ?? Math.round(Date.now() / 1000);

		const normalPositions = currentAccount.openPerpPositions
			.filter((pos) => !pos.baseSize.eq(ZERO))
			.map((pos) => {
				return {
					...pos,
					accountName: currentAccount.name,
					accountKey: currentAccount.userKey,
					isLpPosition: lpMarketIndexes.includes(pos.marketIndex),
					isDustPosition: pos.baseSize
						.abs()
						.lt(
							perpMarketInfoLookup[pos.marketIndex].account.amm.orderStepSize
						),
					showLpEntryPriceTooltip:
						lpMarketIndexes.includes(pos.marketIndex) &&
						lastLpAdded < Env.balEntryPriceCutoffTs,
				};
			});

		return normalPositions;
	}

	const { switchMarket } = useDriftActions();

	const tradeFormState = useDriftStore((s) => s.tradeForm);

	const setActiveUser = (userKey: string) => {
		const { userId, userAuthority } =
			COMMON_UI_UTILS.getIdAndAuthorityFromKey(userKey);
		driftClient?.switchActiveUser(userId, userAuthority);
		setAccountStore((s) => {
			s.currentUserKey = userKey;
		});
	};

	const handleToggleShowOnlyCurrentMarket = () => {
		setStore((s) => {
			s.showOnlyCurrentMarket = !s.showOnlyCurrentMarket;
		});
	};

	const showCloseallPositionsModal = useCallback(() => {
		setStore((s) => {
			s.modals.showCloseAllPositionsModal = true;
		});
	}, []);

	const handleClosePosition = useCallback(
		({ accountKey, market, closePositionOrderType }: ClosePositionOptions) => {
			// don't believe there can be a case where they're closing but not already active user? do it in case
			setActiveUser(accountKey);

			const popupTargetId = `closePosition_${market.toString()}_${accountKey}`;

			showClosePositionPopup({
				marketIndex: market,
				popupTargetId,
				popupOrderType: closePositionOrderType,
				popupType: 'closePosition',
			});
		},
		[]
	);

	const showPnlModal = useCallback((position: OpenPosition, pnl: number) => {
		setStore((s) => {
			s.pnlModalPosition = {
				position,
				actualExitPrice: BigNum.from(
					position.exitPrice,
					PRICE_PRECISION_EXP
				).toNum(),
				pnl,
				isClosed: false,
			};
		});
		actions.showModal('showPnlModal');
	}, []);

	const showLpModal = useCallback((marketIndex: number) => {
		actions.switchMarket({
			marketIndex,
			marketType: MarketType.PERP,
		});
		actions.showModal('showAddLiquidityModal');
	}, []);

	const showLpDetailsModal = useCallback((marketIndex: number) => {
		actions.switchMarket({
			marketIndex,
			marketType: MarketType.PERP,
		});
		actions.showModal('showLpDetailsModal');
	}, []);

	const copySize = useCallback(
		(marketIndex: number) => (size: string) => {
			setStore((s) => {
				if (
					ENUM_UTILS.match(
						s.selectedMarket.current.marketType,
						MarketType.PERP
					) &&
					s.selectedMarket.current.market.marketIndex === marketIndex
				) {
					s.tradeForm.leadSide = 'base';
					s.tradeForm.baseSizeStringValue = size;
				}
			});
		},
		[]
	);

	const copyTradePrice = useCallback(
		(size: number) => {
			if (tradeFormState.orderType !== 'market') {
				if (
					tradeFormState.orderType === 'takeProfitLimit' ||
					tradeFormState.orderType === 'stopLimit'
				) {
					setStore((s) => {
						s.tradeForm.secondaryPriceBoxStringValue = size.toString();
					});
				} else {
					setStore((s) => {
						s.tradeForm.priceBoxStringValue = size.toString();
					});
				}
			}
		},
		[tradeFormState]
	);

	const handleSelection = useCallback((market: PerpMarketConfig) => {
		switchMarket({
			marketIndex: market.marketIndex,
			marketType: MarketType.PERP,
			goToTradePage: true,
		});
	}, []);

	// Store pnl display type preference in local storage
	const [pnlReference, setPnlReference] = useLocalStorageState<
		'mark' | 'oracle'
	>('pnlReference', 'mark');

	const togglePnlReference = () => {
		setPnlReference(pnlReference === 'mark' ? 'oracle' : 'mark');
	};

	const isMobile = useIsMobile();

	const headers = props.small
		? ['perps', 'size', 'p&l', 'close']
		: [
				'market',
				'size',
				`entry/${pnlReference}`,
				'p&l',
				'Take Profit',
				'Stop Loss',
				'liq price',
				'close',
		  ];

	const [currentPage, setCurrentPage] = useState(0);
	const [firstPageShown, setFirstPageShown] = useState(0);

	const executePageChange = (newPage: number, oldFirstPageShown: number) => {
		if (newPage === currentPage) return;
		setCurrentPage(newPage);
		setFirstPageShown(oldFirstPageShown);
	};

	return (
		<>
			<PaginatedTableV2
				tableGrid={tableGrid}
				pageSize={pageSize}
				currentPage={currentPage}
				firstPageShown={firstPageShown}
				totalItems={filteredPositions?.length ?? 0}
				pageChangeCallback={executePageChange}
				noBorder
				tableState={{
					records: displayPositions,
					emptyStateText: 'No open positions found',
					requireAccountCreated: true,
					requireWalletConnect: true,
					id: 'open_positions_table',
				}}
				tableContentTop={
					isMobile ? null : (
						<TableV2.HeaderRow
							forceBottomBorder
							grid={props.small ? smallTableGrid : tableGrid}
							header
						>
							{headers.map((label) => (
								<TableV2.HeaderCell
									className={`${
										props.overviewPage ? 'bg-main-bg' : ''
									} capitalize`}
									key={`header_${label}`.replace(/ /g, '')}
								>
									{label === 'p&l' ? (
										<>
											<Tooltip
												placement="top"
												className="flex items-center h-full"
												content={
													<div>
														P&L is based on {pnlReference} price.
														<br /> <br /> Click on the header to toggle P&L to
														reflect{' '}
														{pnlReference === 'mark' ? 'oracle' : 'mark'} price.
													</div>
												}
											>
												<Button
													size="MEDIUM"
													className="justify-start h-full px-0 py-0"
													onClick={togglePnlReference}
												>
													<div className="underline capitalize">{label}</div>
													<img
														src={`/assets/icons/pnlCash.svg`}
														className="w-4 h-4"
													/>
												</Button>
											</Tooltip>
										</>
									) : label === 'liq price' ? (
										<>
											<Tooltip
												placement="top"
												content={
													<div className="flex flex-col space-y-2 text-xs">
														<span>
															Liquidation Price is the Oracle Price of the asset
															where your account can be liquidated, assuming the
															prices of your other assets/positions are
															constant.
														</span>
													</div>
												}
												allowHover
											>
												<span>{label}</span>
											</Tooltip>
										</>
									) : (
										<>{label}</>
									)}
								</TableV2.HeaderCell>
							))}
						</TableV2.HeaderRow>
					)
				}
				tableContentMiddle={
					<div className="w-full text-xs sm:max-h-full">
						{filteredPositions
							?.slice(currentPage * pageSize, currentPage * pageSize + pageSize)
							?.filter((position) => !!position)
							?.map((position) => {
								const key = `${position.accountKey}-${position.direction}-${position.marketSymbol}-${position.baseSize}-${position.entryPrice}`;
								return (
									<OpenPositionTableRow
										position={position}
										isMobile={isMobile}
										pnlReference={pnlReference}
										small={props.small}
										smallTableGrid={smallTableGrid}
										tableGrid={tableGrid}
										handleClosePosition={handleClosePosition}
										handleSelection={handleSelection}
										handleCopySize={copySize(position.marketIndex)}
										handleCopyTradePrice={copyTradePrice}
										handleShowPnlModal={showPnlModal}
										handleEditLp={showLpModal}
										handleViewLpDetails={showLpDetailsModal}
										key={key}
									/>
								);
							})}
					</div>
				}
				tableContentBottom={
					displayPositions?.length > 0 ? (
						<TableV2.SummaryRow
							className={'border-t border-container-border'}
							grid={''}
						>
							{props.overviewPage ? (
								<div className={`items-center w-full flex justify-end p-1`}>
									<Button.Secondary
										size="SMALL"
										disabled={!filteredPositions || !filteredPositions.length}
										onClick={showCloseallPositionsModal}
										negativeRed
									>
										{`Close ${filteredPositions?.length || 0} Positions`}
									</Button.Secondary>
								</div>
							) : (
								<div className={`items-center w-full flex justify-between p-1`}>
									<CheckboxInput
										checked={showOnlyCurrentMarket}
										onChange={handleToggleShowOnlyCurrentMarket}
										label="Show only selected market"
										className="pr-4 text-text-default"
										secondaryStyle
									/>
									<Button.Secondary
										size="SMALL"
										disabled={!filteredPositions || !filteredPositions.length}
										onClick={showCloseallPositionsModal}
										negativeRed
										className="whitespace-nowrap"
									>
										{`Close ${filteredPositions?.length || 0} Position${
											filteredPositions?.length > 1 ? 's' : ''
										}`}
									</Button.Secondary>
								</div>
							)}
						</TableV2.SummaryRow>
					) : null
				}
			/>

			{showClosePositionPopupForMarketIndex !== undefined && (
				<ClosePositionPopup
					targetElementId={closePositionPopupTargetId}
					marketIndex={showClosePositionPopupForMarketIndex}
					onClose={() => hideClosePositionPopup()}
					uiOrderType={closePositionOrderType}
					isOverviewPage={props.overviewPage}
					renderSide={props.overviewPage ? 'left' : 'leftTop'}
					isStopLossOnly={closePositionPopupType === 'stopLoss'}
					isTakeProfitOnly={closePositionPopupType === 'takeProfit'}
				/>
			)}

			{orderToEdit && editOrderTargetElementId && (
				<EditOrderPopup
					targetElementId={editOrderTargetElementId}
					existingOrder={orderToEdit}
					onClose={() => hideEditOrderPopup()}
				/>
			)}
		</>
	);
};

export default React.memo(OpenPositionsTable);
